import { useRef, useEffect } from 'react'
import type { SessionMessage, StreamingMessage } from '../types'
import { ChatBubble } from './ChatBubble'
import { TypingIndicator } from './TypingIndicator'

interface ChatContainerProps {
  messages: SessionMessage[]
  streamingMessage: StreamingMessage | null
  isLoading?: boolean
  onSendMessage?: (content: string) => void
}

function MessageSkeleton({ align }: { align: 'left' | 'right' }) {
  return (
    <div className={`flex items-end gap-2.5 px-4 py-1.5 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
      <div className="w-7 h-7 rounded-full bg-gray-200 animate-pulse shrink-0" />
      <div
        className={`rounded-2xl h-12 animate-pulse ${align === 'right'
            ? 'w-44 bg-[var(--color-primary-light)] rounded-br-sm'
            : 'w-56 bg-gray-100 rounded-bl-sm'
          }`}
      />
    </div>
  )
}

export function ChatContainer({
  messages,
  streamingMessage,
  isLoading = false,
  onSendMessage,
}: ChatContainerProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingMessage])

  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto py-4">
        <MessageSkeleton align="left" />
        <MessageSkeleton align="right" />
        <MessageSkeleton align="left" />
      </div>
    )
  }

  if (messages.length === 0 && !streamingMessage) {
    return (
      <div className="flex-1 overflow-y-auto flex items-center justify-center">
        <div className="text-center px-8 py-12">
          <div className="w-14 h-14 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center mx-auto mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-7 h-7 text-[var(--color-primary)]"
            >
              <path
                fillRule="evenodd"
                d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97zM6.75 8.25a.75.75 0 01.75-.75h9a.75.75 0 010 1.5h-9a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H7.5z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="text-sm font-medium text-[var(--color-neutral-700)]">
            Commencez la conversation
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Envoyez votre premier message pour demarrer le cours
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto py-4 space-y-1">
      {messages.filter((msg) => msg.role !== 'system').map((msg) => (
        <ChatBubble key={msg.id} message={msg} onSendMessage={onSendMessage} />
      ))}

      {/* Typing indicator: streaming started but no content yet */}
      {streamingMessage?.isStreaming && !streamingMessage.content && (
        <TypingIndicator />
      )}

      {/* Streaming bubble: content arriving */}
      {streamingMessage && streamingMessage.content.length > 0 && (
        <ChatBubble message={streamingMessage} onSendMessage={onSendMessage} />
      )}

      <div ref={bottomRef} aria-hidden="true" />
    </div>
  )
}
